/**
 * GitHubIntegration
 * GitHub App connection status, install flow, and accessible repos browser.
 */

import React, { useState, useEffect } from 'react';
import { Github, ExternalLink, Check, AlertCircle, Unplug, RefreshCw, Plus, Lock, Unlock, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import {
  useGitHubStatus,
  useGitHubRepos,
  useInstallGitHub,
  useUninstallGitHub,
  useCreateGitHubRepo,
} from '../../api/workspace-github';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useToast } from '../../contexts/ToastContext';

const GITHUB_APP_SLUG = 'martha-github-application-dev'; // GitHub App slug for install URL

export const GitHubIntegration: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: status, isLoading, refetch } = useGitHubStatus(workspaceId);
  const installMutation = useInstallGitHub();
  const uninstallMutation = useUninstallGitHub();
  const [repoSearch, setRepoSearch] = useState('');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Handle GitHub App callback
  useEffect(() => {
    const installationId = searchParams.get('installation_id');
    const setupAction = searchParams.get('setup_action');

    if (installationId && setupAction === 'install' && workspaceId) {
      installMutation.mutate(
        { workspaceId, installationId: parseInt(installationId) },
        {
          onSuccess: () => {
            // Clean up URL params
            setSearchParams({}, { replace: true });
            refetch();
          },
        },
      );
    }
  }, [searchParams, workspaceId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-surface-subtle rounded-lg animate-pulse" />
        <div className="h-48 bg-surface-subtle rounded-lg animate-pulse" />
      </div>
    );
  }

  const isConnected = status?.connected ?? false;

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <Panel>
        <PanelHeader title="Connection" />
        <PanelBody>
          {isConnected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check size={16} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {status?.orgAvatarUrl && (
                      <img src={status.orgAvatarUrl} alt="" className="w-5 h-5 rounded-full" />
                    )}
                    <span className="text-[13px] font-medium text-emerald-800">
                      Connected to {status?.orgLogin}
                    </span>
                  </div>
                  <span className="text-[11px] text-emerald-600">
                    Installed {status?.installedAt ? new Date(status.installedAt).toLocaleDateString() : ''}
                  </span>
                </div>
                <button
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="px-3 py-1.5 text-[12px] text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Unplug size={13} className="inline mr-1" />
                  Disconnect
                </button>
              </div>

              {showDisconnectConfirm && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
                  <p className="text-[12px] text-red-700">
                    This will remove the GitHub connection. Projects using this integration will lose access to repository features.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (workspaceId) {
                          uninstallMutation.mutate({ workspaceId }, {
                            onSuccess: () => {
                              setShowDisconnectConfirm(false);
                              refetch();
                            },
                          });
                        }
                      }}
                      disabled={uninstallMutation.isPending}
                      className="px-3 py-1.5 text-[12px] text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Confirm Disconnect
                    </button>
                    <button
                      onClick={() => setShowDisconnectConfirm(false)}
                      className="px-3 py-1.5 text-[12px] text-muted hover:bg-surface-subtle rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {!status?.appConfigured && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-[12px] text-amber-700">
                    GitHub App not configured on the server. Set <code className="text-[11px] bg-amber-100 px-1 rounded">GITHUB_APP_ID</code> and <code className="text-[11px] bg-amber-100 px-1 rounded">GITHUB_APP_PRIVATE_KEY</code> environment variables.
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-surface-muted border border-line rounded-lg">
                <div className="w-8 h-8 rounded-full bg-surface-subtle flex items-center justify-center">
                  <Github size={16} className="text-muted" />
                </div>
                <div className="flex-1">
                  <span className="text-[13px] font-medium text-text">Not connected</span>
                  <p className="text-[11px] text-muted">
                    Install the Martha GitHub App to enable repository integration.
                  </p>
                </div>
                <a
                  href={`https://github.com/apps/${GITHUB_APP_SLUG}/installations/new`}
                  className="px-4 py-2 text-[13px] font-medium text-white bg-neutral-800 hover:bg-neutral-900 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Github size={14} />
                  Install GitHub App
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Repository Access */}
      {isConnected && (
        <ReposBrowser workspaceId={workspaceId!} repoSearch={repoSearch} onSearchChange={setRepoSearch} />
      )}
    </div>
  );
};

// ─── Repos Browser ──────────────────────────────────────────────

function ReposBrowser({
  workspaceId,
  repoSearch,
  onSearchChange,
}: {
  workspaceId: string;
  repoSearch: string;
  onSearchChange: (v: string) => void;
}) {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const { data, isLoading, refetch } = useGitHubRepos(workspaceId, { page, search: repoSearch || undefined });
  const createMutation = useCreateGitHubRepo();

  // Create repo form state
  const [showCreate, setShowCreate] = useState(false);
  const [repoName, setRepoName] = useState('');
  const [repoDesc, setRepoDesc] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const repoNameValid = /^[a-z0-9._-]+$/i.test(repoName);

  const handleCreate = () => {
    if (!repoName.trim() || !repoNameValid) return;
    createMutation.mutate(
      { workspaceId, name: repoName.trim(), description: repoDesc.trim() || undefined, isPrivate },
      {
        onSuccess: () => {
          toast.success(`Repository "${repoName}" created`);
          setRepoName('');
          setRepoDesc('');
          setIsPrivate(true);
          setShowCreate(false);
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to create repository');
        },
      },
    );
  };

  return (
    <Panel>
      <PanelHeader
        title="Repository Access"
        actions={
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCreate(!showCreate)}
              className={`flex items-center gap-1 px-2 py-1 text-[12px] font-medium rounded-lg transition-colors ${
                showCreate ? 'bg-coral-50 text-coral-600' : 'text-muted hover:text-text hover:bg-surface-subtle'
              }`}
              title="Create new repository"
            >
              <Plus size={13} />
              New Repo
            </button>
            <button
              onClick={() => refetch()}
              className="p-1 text-subtle hover:text-muted rounded transition-colors"
              title="Refresh repos"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        }
      />
      <PanelBody>
        <div className="space-y-3">
          {/* Create Repo Form */}
          {showCreate && (
            <div className="p-3 bg-surface-muted border border-line rounded-lg space-y-3 animate-fade-in">
              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider">
                  Repository Name
                </label>
                <input
                  type="text"
                  value={repoName}
                  onChange={(e) => setRepoName(e.target.value)}
                  placeholder="my-new-repo"
                  className={`w-full mt-1 px-3 py-1.5 border rounded-lg text-[13px] focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none ${
                    repoName && !repoNameValid ? 'border-red-300 bg-red-50' : 'border-line'
                  }`}
                />
                {repoName && !repoNameValid && (
                  <p className="text-[10px] text-red-500 mt-0.5">Only letters, numbers, hyphens, dots, and underscores</p>
                )}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-muted uppercase tracking-wider">
                  Description <span className="font-normal text-subtle">(optional)</span>
                </label>
                <input
                  type="text"
                  value={repoDesc}
                  onChange={(e) => setRepoDesc(e.target.value)}
                  placeholder="A brief description..."
                  className="w-full mt-1 px-3 py-1.5 border border-line rounded-lg text-[13px] focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setIsPrivate(!isPrivate)}
                  className="flex items-center gap-1.5 text-[12px] text-muted hover:text-text transition-colors"
                >
                  {isPrivate ? <Lock size={13} /> : <Unlock size={13} />}
                  {isPrivate ? 'Private' : 'Public'}
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setShowCreate(false); setRepoName(''); setRepoDesc(''); }}
                    className="px-3 py-1.5 text-[12px] text-muted hover:text-text hover:bg-surface-subtle rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={!repoName.trim() || !repoNameValid || createMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}

          <input
            type="text"
            value={repoSearch}
            onChange={(e) => { onSearchChange(e.target.value); setPage(1); }}
            placeholder="Filter repositories..."
            className="w-full px-3 py-2 border border-line rounded-lg text-[13px] focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none"
          />

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-surface-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : data?.repos?.length ? (
            <>
              <div className="text-[11px] text-subtle mb-2">
                {data.totalCount} accessible repositor{data.totalCount === 1 ? 'y' : 'ies'}
              </div>
              <div className="divide-y divide-neutral-100">
                {data.repos.map(repo => (
                  <div key={repo.id} className="py-2 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-text truncate">
                          {repo.full_name}
                        </span>
                        {repo.private && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-surface-subtle text-muted">
                            private
                          </span>
                        )}
                      </div>
                      {repo.description && (
                        <p className="text-[11px] text-muted truncate">{repo.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {repo.language && (
                        <span className="text-[10px] text-subtle">{repo.language}</span>
                      )}
                      <a
                        href={repo.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-subtle hover:text-muted transition-colors"
                      >
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {data.totalCount > 30 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1 text-[12px] text-muted hover:text-text disabled:opacity-30 rounded"
                  >
                    Previous
                  </button>
                  <span className="text-[12px] text-subtle">Page {page}</span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={data.repos.length < 30}
                    className="px-3 py-1 text-[12px] text-muted hover:text-text disabled:opacity-30 rounded"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-[12px] text-subtle text-center py-4">
              No repositories found
            </p>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
}
